import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolder,
  faRocket,
  faStop,
  faTrash,
  faXmark,
  faFileAlt,
  faCheck,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { buildPromptForBatch, buildPromptOcrEnglish, buildPromptEnglishGrading } from "../utils/buildPrompt";
import { buildPromptUrdu, buildPromptUrduOcr, buildPromptUrduGrading } from "../utils/buildPromptUrdu";
import { folderDisplayTitle } from "../utils/folderDisplay";
import { stripQuestionPaperText } from "../utils/questionPaper";
import TwoStepAiControls from "./TwoStepAiControls";
import { LOCAL_MODELS, isKnownLocalModel } from "../utils/localModels";
import ProviderSelect from "./ProviderSelect";
import { CHECK_PROVIDER_KEY, readPageProvider, writePageProvider } from "../utils/pageProvider";
import useAiStatus from "../utils/useAiStatus";
import "../App.css";

const API_BASE_URL = window.API_BASE_URL;

/** Parse "1,3,2" → [1, 3, 2]. Ignores blanks / non-positive values. */
function parseBatchSizes(raw) {
  if (!raw || !String(raw).trim()) return [];
  return String(raw)
    .split(",")
    .map((s) => parseInt(String(s).trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0);
}

/**
 * Resolve sheet counts for Recheck grouping.
 * - "1,3,2" → [1, 3, 2] (per-student sheets; each sheet = 2 images)
 * - "1" (no commas) → repeat that sheet count across all selected images
 *   e.g. 8 images + "1" → [1,1,1,1] (four students × 2 images)
 */
function resolveSheetCounts(parsed, selectedCount) {
  if (!parsed.length) return [];
  if (parsed.length === 1) {
    const sheets = parsed[0];
    const imgsPer = sheets * 2;
    if (!selectedCount || imgsPer <= 0 || selectedCount % imgsPer !== 0) return [];
    const n = selectedCount / imgsPer;
    return Array.from({ length: n }, () => sheets);
  }
  return parsed;
}

const dateKey = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const snapshotBadge = (status) => {
  if (status === "completed") return { text: "EDUPAGED", color: "#16a34a" };
  if (status === "copy_to_edupage") return { text: "COPY TO EDUPAGE", color: "#2563eb" };
  if (status === "auto-checked") return { text: "AUTO-CHECKED", color: "#7c3aed" };
  if (status === "reviewed") return { text: "REVIEWED", color: "#059669" };
  if (status === "error") return { text: "CHECK FAILED", color: "#dc2626" };
  return { text: "REVIEW", color: "#d97706" };
};

/**
 * Compact Sort-style folder browser used by Recheck. It deliberately mirrors
 * the Sort tab's 30-day calendar, filters, status colours, and five-card grid,
 * but selecting a card opens its raw scan images instead of sorted rows.
 */
function RecheckFolderBrowser({ folders, selectedFolderId, onSelect, onMessage }) {
  const [filterTeacher, setFilterTeacher] = useState("");
  const [filterClass, setFilterClass] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [filterSorted, setFilterSorted] = useState("all");
  const [filterChecked, setFilterChecked] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);
  const [showFuture, setShowFuture] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [snapshotMap, setSnapshotMap] = useState(new Map());
  const [eduBridgeReady, setEduBridgeReady] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${API_BASE_URL}/analyze/snapshots/folders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const rows = data.data || data.folders || [];
        if (active) {
          setSnapshotMap(new Map(rows.map((r) => [
            String(r.folder_path || "").replace(/^rubrics\//, ""),
            r.status || "saved",
          ])));
        }
      } catch (err) {
        console.error("Failed to load folder snapshot statuses:", err);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const onMsg = (event) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || data.source !== "edupage-selector") return;
      if (data.type === "EDU_BRIDGE_READY") {
        setEduBridgeReady(true);
      } else if (data.type === "EDU_LOGIN_RESULT") {
        if (data.ok) onMessage(`Opening EduPage for ${data.username || "this account"}…`, "success");
        else onMessage(
          data.reason === "not_found"
            ? "This folder creator is not in the Login Edupage credential list."
            : "EduPage login failed — install/update the Login Edupage extension.",
          "error"
        );
      }
    };
    window.addEventListener("message", onMsg);
    window.postMessage({ source: "fastgrader-edu", type: "EDU_BRIDGE_PING" }, "*");
    return () => window.removeEventListener("message", onMsg);
  }, [onMessage]);

  const loginToEduPage = (event, folder) => {
    event.stopPropagation();
    const card = event.currentTarget.closest("[data-edu-card]");
    const text = card ? card.innerText : "";
    if (!text.trim()) return;
    window.postMessage({ source: "fastgrader-edu", type: "EDU_LOGIN_CARD", text }, "*");
    onMessage(
      eduBridgeReady
        ? `Logging into EduPage for ${folder.created_by || "this folder"}…`
        : "Sent to Login Edupage — if nothing opens, install/update the extension and reload.",
      "info"
    );
  };

  const options = (field) => [...new Set(folders.map((f) => f[field]).filter(Boolean))]
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" }));

  const teacherOptions = useMemo(() => options("created_by"), [folders]);
  const classOptions = useMemo(() => options("class"), [folders]);
  const subjectOptions = useMemo(() => options("subject"), [folders]);

  const visibleFolders = useMemo(() => folders.filter((f) => {
    if (filterTeacher && f.created_by !== filterTeacher) return false;
    if (filterClass && f.class !== filterClass) return false;
    if (filterSubject && f.subject !== filterSubject) return false;
    const hasSnapshot = snapshotMap.has(String(f.id));
    if (filterSorted === "sorted" && !hasSnapshot) return false;
    if (filterSorted === "unsorted" && hasSnapshot) return false;
    const checked = !!f.completed_at || !!f.printed_at;
    if (filterChecked === "checked" && !checked) return false;
    if (filterChecked === "unchecked" && checked) return false;
    return true;
  }), [folders, filterTeacher, filterClass, filterSubject, filterSorted, filterChecked, snapshotMap]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const calendarDays = useMemo(() => {
    const days = [];
    const start = new Date(today);
    if (showFuture) {
      start.setDate(start.getDate() + 1);
    } else {
      start.setDate(start.getDate() - 29);
    }
    for (let i = 0; i < 30; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [today, showFuture]);

  const folderCountForDate = (date) =>
    visibleFolders.filter((f) => dateKey(f.test_date || f.created_at) === dateKey(date)).length;

  const futureCount = visibleFolders.filter((f) => {
    const d = new Date(f.test_date || f.created_at || 0);
    d.setHours(0, 0, 0, 0);
    return d > today;
  }).length;

  const displayedFolders = visibleFolders.filter((f) => {
    const d = new Date(f.test_date || f.created_at || 0);
    d.setHours(0, 0, 0, 0);
    if (showFuture) return d > today;
    if (selectedDate) return dateKey(d) === dateKey(selectedDate);
    return true;
  });

  const rangeLabel = `${calendarDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${calendarDays[calendarDays.length - 1].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  const filtersActive = filterTeacher || filterClass || filterSubject || filterSorted !== "all" || filterChecked !== "all";
  const resetFilters = () => {
    setFilterTeacher("");
    setFilterClass("");
    setFilterSubject("");
    setFilterSorted("all");
    setFilterChecked("all");
  };

  return (
    <div className="card" style={{ marginBottom: "var(--spacing-xl)", padding: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: collapsed ? 0 : 8 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 8, color: "white",
          background: "linear-gradient(135deg,#10b981,#059669)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <FontAwesomeIcon icon={faFolder} />
        </div>
        <div style={{ marginRight: "auto" }}>
          <div style={{ fontSize: "1rem", fontWeight: 800, color: "var(--text-primary)" }}>1. Select Folder</div>
          <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>Choose a folder, then select its raw images below</div>
        </div>

        <button type="button" onClick={() => { setShowFuture((v) => !v); setSelectedDate(null); }}
          style={topToggleStyle(showFuture, "#7c3aed")}>
          <i className="fa-solid fa-forward" /> Future Cards
          {futureCount > 0 && <span style={countPillStyle}>{futureCount}</span>}
        </button>

        <FilterSelect icon="fa-user-tie" color="#8b5cf6" label="Teacher:"
          value={filterTeacher} onChange={setFilterTeacher} options={teacherOptions} allLabel="All Teachers" />
        <FilterSelect icon="fa-graduation-cap" color="#3b82f6" label="Class:"
          value={filterClass} onChange={setFilterClass} options={classOptions} allLabel="All Classes" />
        <FilterSelect icon="fa-book" color="#10b981" label="Subject:"
          value={filterSubject} onChange={setFilterSubject} options={subjectOptions} allLabel="All Subjects" />

        <div style={{ display: "inline-flex", padding: 2, borderRadius: 7, background: "var(--tab-bg,rgba(0,0,0,.04))" }}>
          <button type="button" onClick={() => { setSelectedDate(null); setShowFuture(false); }}
            style={segmentButton(!selectedDate && !showFuture, "#2563eb")}>All</button>
          <button type="button" onClick={() => { setSelectedDate(today); setShowFuture(false); }}
            style={segmentButton(selectedDate && dateKey(selectedDate) === dateKey(today), "#2563eb")}>Today</button>
        </div>

        {filtersActive && <button type="button" onClick={resetFilters} style={topToggleStyle(true, "#d97706")}>
          <i className="fa-solid fa-filter-circle-xmark" /> Clear
        </button>}
        <button type="button" onClick={() => setCollapsed((v) => !v)} style={topToggleStyle(false, "#64748b")}>
          <i className={`fa-solid fa-${collapsed ? "eye" : "eye-slash"}`} />
          {collapsed ? "Show folders" : "Hide folders"}
        </button>
      </div>

      {!collapsed && (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div style={{
            width: 290, flexShrink: 0, padding: 6, borderRadius: 10,
            border: "1px solid var(--border-color,#e5e7eb)", background: "var(--card-bg,white)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <strong style={{ fontSize: "0.76rem" }}>Select Date</strong>
              <span style={{ fontSize: "0.6rem", fontWeight: 700, color: "#15803d", border: "1px solid rgba(16,185,129,.4)", background: "rgba(16,185,129,.1)", borderRadius: 9, padding: "2px 6px" }}>
                {rangeLabel}
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 4 }}>
              {calendarDays.map((day) => {
                const count = folderCountForDate(day);
                const selected = selectedDate && dateKey(selectedDate) === dateKey(day);
                const isToday = dateKey(today) === dateKey(day);
                return (
                  <button key={dateKey(day)} type="button"
                    onClick={() => { if (count) { setSelectedDate(day); setShowFuture(false); } }}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      minHeight: 47, padding: "4px 2px", borderRadius: 7,
                      border: selected ? "2px solid #2563eb" : isToday ? "2px solid #93c5fd" : count ? "1px solid rgba(16,185,129,.3)" : "1px solid transparent",
                      background: selected ? "linear-gradient(135deg,#3b82f6,#2563eb)" : isToday ? "rgba(59,130,246,.07)" : count ? "rgba(16,185,129,.06)" : "transparent",
                      color: selected ? "white" : "var(--text-primary)", cursor: count ? "pointer" : "default",
                    }}>
                    <span style={{ display: "block", width: "100%", fontSize: "0.55rem", fontWeight: 800, lineHeight: 1, textTransform: "uppercase", opacity: 0.8 }}>
                      {day.toLocaleDateString("en-US", { weekday: "short" })}
                    </span>
                    <span style={{ display: "block", width: "100%", fontSize: "0.9rem", fontWeight: 700, lineHeight: 1.15 }}>{day.getDate()}</span>
                    {count > 0 && <span style={{ display: "block", width: "100%", fontSize: "0.52rem", lineHeight: 1, color: selected ? "white" : "#2563eb", fontWeight: 700 }}>{count} Folder{count !== 1 ? "s" : ""}</span>}
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: "0.6rem", color: "var(--text-muted)" }}>
              <span style={{ width: 10, height: 8, background: "rgba(16,185,129,.08)", border: "1px solid rgba(16,185,129,.3)" }} /> Has folders
              <span style={{ width: 10, height: 8, background: "rgba(59,130,246,.08)", border: "1px solid #93c5fd" }} /> Today
              {selectedDate && <button type="button" onClick={() => setSelectedDate(null)}
                style={{ marginLeft: "auto", border: 0, borderRadius: 5, background: "#ef4444", color: "white", fontSize: "0.57rem", padding: "2px 5px", cursor: "pointer" }}>× Clear Date</button>}
            </div>
            <div style={{ display: "flex", gap: 4, marginTop: 7 }}>
              <SegmentGroup values={["all", "sorted", "unsorted"]} value={filterSorted} onChange={setFilterSorted} color="#059669" />
              <SegmentGroup values={["all", "checked", "unchecked"]} value={filterChecked} onChange={setFilterChecked} color="#2563eb" />
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 800, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 5 }}>
              <i className="fa-solid fa-folder-open" style={{ color: "#f59e0b", marginRight: 5 }} />
              {displayedFolders.length} folder{displayedFolders.length !== 1 ? "s" : ""}
              <span style={{ textTransform: "none", marginLeft: 5 }}>· {showFuture ? "From tomorrow onwards" : selectedDate ? dateKey(selectedDate) : "All dates"}</span>
            </div>
            <div style={{
              display: "grid",
              // Five cards still fit on wide screens like Sort. At narrower
              // widths the grid drops to four/three columns instead of
              // crushing text into overlapping 120px cards.
              gridTemplateColumns: "repeat(auto-fit,minmax(175px,1fr))",
              gap: 8,
              alignItems: "stretch",
            }}>
              {displayedFolders.map((folder) => {
                const selected = String(folder.id) === String(selectedFolderId);
                const empty = !Number(folder.scan_count);
                const printed = !!folder.printed_at;
                const done = !printed && !!folder.completed_at;
                const status = snapshotMap.get(String(folder.id));
                const badge = status ? snapshotBadge(status) : null;
                const accent = selected ? "#f59e0b" : empty ? "#ef4444" : printed ? "#2196f3" : done ? "#22c55e" : "#e5e7eb";
                const bg = selected ? "rgba(245,158,11,.12)" : empty ? "rgba(239,68,68,.07)" : printed ? "rgba(33,150,243,.07)" : done ? "rgba(34,197,94,.07)" : "var(--card-bg,white)";
                return (
                  <div key={folder.id} data-edu-card="1" role="button" tabIndex={0}
                    onClick={() => onSelect(String(folder.id))}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onSelect(String(folder.id)); }}
                    style={{
                      width: "100%", minWidth: 0, minHeight: 138, textAlign: "left",
                      padding: 9, borderRadius: 9, boxSizing: "border-box",
                      border: `${selected || empty ? 2 : 1}px solid ${accent}`, background: bg,
                      cursor: "pointer", boxShadow: "0 2px 7px rgba(0,0,0,.07)",
                      color: "var(--text-primary)", overflow: "hidden",
                      whiteSpace: "normal", verticalAlign: "top",
                    }}>
                    <div style={{ display: "flex", gap: 7, minWidth: 0, overflow: "hidden" }}>
                      <i className="fa-solid fa-folder" style={{ color: selected ? "#d97706" : empty ? "#ef4444" : "#f59e0b", fontSize: 20, flexShrink: 0 }} />
                      <div style={{ minWidth: 0, maxWidth: "100%", flex: 1, overflow: "hidden" }}>
                        <div title={folder.title} style={{ fontSize: "0.75rem", fontWeight: status ? 800 : 700, color: selected ? "#b45309" : status ? "#059669" : "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {folderDisplayTitle(folder) || `${folder.class || ""} - ${folder.subject || ""}`}
                        </div>
                        {folder.assignment_name && <div style={cardMetaStyle("#2563eb")}><i className="fa-solid fa-tag" /> <span style={ellipsisStyle}>{folder.assignment_name}</span></div>}
                        <div style={cardMetaStyle()}><i className="fa-solid fa-calendar-day" /> {new Date(folder.test_date || folder.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                        <div style={cardMetaStyle(empty ? "#dc2626" : undefined)}><i className="fa-solid fa-file-image" /> {folder.scan_count || 0} images</div>
                        {folder.created_by && <div style={cardMetaStyle()}><i className="fa-solid fa-user" /> <span style={ellipsisStyle}>{folder.created_by}</span></div>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 7, maxWidth: "100%", overflow: "hidden" }}>
                      {folder.created_by && (
                        <button type="button" onClick={(e) => loginToEduPage(e, folder)}
                          title={eduBridgeReady ? `Log into EduPage as ${folder.created_by}` : "Requires Login Edupage extension"}
                          style={{ border: "1px solid #059669", color: "#059669", background: "transparent", borderRadius: 5, padding: "2px 5px", fontSize: "0.56rem", fontWeight: 800, cursor: "pointer" }}>
                          <i className="fa-solid fa-right-to-bracket" /> LOGIN
                        </button>
                      )}
                      <span style={{ border: "1px solid #2563eb", color: "#2563eb", borderRadius: 5, padding: "2px 5px", fontSize: "0.56rem", fontWeight: 800 }}>
                        {selected ? "SELECTED" : "SELECT IMAGES"}
                      </span>
                      {badge && <span style={{ color: "white", background: badge.color, borderRadius: 8, padding: "2px 6px", fontSize: "0.54rem", fontWeight: 800 }}>{badge.text}</span>}
                      {printed && <span style={{ color: "white", background: "#2196f3", borderRadius: 8, padding: "2px 6px", fontSize: "0.54rem", fontWeight: 800 }}>PRINTED</span>}
                      {done && !badge && <span style={{ color: "white", background: "#16a34a", borderRadius: 8, padding: "2px 6px", fontSize: "0.54rem", fontWeight: 800 }}>READY TO PRINT</span>}
                      {empty && <span style={{ color: "white", background: "#ef4444", borderRadius: 8, padding: "2px 6px", fontSize: "0.54rem", fontWeight: 800 }}>EMPTY</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            {displayedFolders.length === 0 && <div style={{ padding: 20, color: "var(--text-muted)", fontStyle: "italic" }}>No folders for this filter.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ icon, color, label, value, onChange, options, allLabel }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.68rem", fontWeight: 700 }}>
      <i className={`fa-solid ${icon}`} style={{ color }} /> {label}
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{ minWidth: 105, maxWidth: 155, padding: "5px 7px", border: "1px solid var(--border)", borderRadius: 7, background: "var(--surface)", color: "var(--text-primary)", fontSize: "0.67rem" }}>
        <option value="">{allLabel}</option>
        {options.map((item) => <option key={item} value={item}>{item}</option>)}
      </select>
    </label>
  );
}

function SegmentGroup({ values, value, onChange, color }) {
  return (
    <div style={{ flex: 1, display: "flex", border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
      {values.map((item) => <button key={item} type="button" onClick={() => onChange(item)}
        style={{ flex: 1, border: 0, padding: "4px 1px", background: value === item ? color : "transparent", color: value === item ? "white" : "var(--text-primary)", fontSize: "0.58rem", fontWeight: 700, textTransform: "capitalize", cursor: "pointer" }}>{item}</button>)}
    </div>
  );
}

const topToggleStyle = (active, color) => ({
  display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 9px",
  border: "1px solid var(--border)", borderRadius: 7, cursor: "pointer",
  background: active ? color : "var(--surface)", color: active ? "white" : "var(--text-primary)",
  fontSize: "0.67rem", fontWeight: 700, whiteSpace: "nowrap",
});
const segmentButton = (active, color) => ({
  border: 0, borderRadius: 5, padding: "4px 9px", cursor: "pointer",
  background: active ? color : "transparent", color: active ? "white" : "var(--text-primary)",
  fontSize: "0.65rem", fontWeight: 700,
});
const countPillStyle = { minWidth: 15, height: 15, padding: "0 4px", borderRadius: 8, background: "rgba(255,255,255,.25)", color: "white", fontSize: 9, display: "inline-flex", alignItems: "center", justifyContent: "center" };
const cardMetaStyle = (color = "var(--text-muted)") => ({
  display: "flex", alignItems: "center", gap: 4, marginTop: 3, minWidth: 0,
  maxWidth: "100%", color, fontSize: "0.6rem", lineHeight: 1.25,
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
});
const ellipsisStyle = { display: "block", minWidth: 0, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/**
 * Recheck tab — folder + image selection (no sorted table). Sheet batches:
 * a single number (e.g. 1) applies to every student; comma lists (e.g. 1,3,2)
 * set per-student sheets. Each sheet = 2 images. Grades via
 * /assess/check-direct → LM Studio / configured provider. After check, rows
 * can be appended (never overwritten) to the assessment snapshot, the saved
 * Sort folder, and Approve.
 */
export default function StudentUploader({ rubricText, pdfRubric }) {
  // ── Folder / images ─────────────────────────────────────────────────────
  const [scansFolders, setScansFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(() => {
    try { return localStorage.getItem("recheckTab_selectedFolderId") || ""; } catch { return ""; }
  });
  const [folderImages, setFolderImages] = useState([]); // [{ file_name, ... }]
  const [selectedImages, setSelectedImages] = useState([]); // file_name[]
  const [imagesLoading, setImagesLoading] = useState(false);

  // ── Batch / model config ────────────────────────────────────────────────
  const [batchSizesRaw, setBatchSizesRaw] = useState("");
  const [subjectType, setSubjectType] = useState("none");
  const [pipelineMode, setPipelineMode] = useState(() =>
    localStorage.getItem("recheckPipelineMode") || "single"
  );
  const [selectedApiKey, setSelectedApiKey] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  // Two-step OCR/grading models + hosts (shared localStorage with Checking tab)
  const [ocrModel, setOcrModel] = useState(() => {
    const saved = localStorage.getItem("urduOcrModel");
    const provider = readPageProvider(CHECK_PROVIDER_KEY);
    const geminiProvider = !["local", "deepseek", "minimax", "openai"].includes(provider);
    if (geminiProvider) {
      return (typeof saved === "string" && saved.startsWith("gemini-")) ? saved : "gemini-3.1-pro-preview";
    }
    if (provider !== "local") return saved || "gemini-2.5-flash";
    return isKnownLocalModel(saved) ? saved : "qwen38_27b";
  });
  const [gradingModel, setGradingModel] = useState(() => {
    const saved = localStorage.getItem("urduGradingModel");
    const provider = readPageProvider(CHECK_PROVIDER_KEY);
    const geminiProvider = !["local", "deepseek", "minimax", "openai"].includes(provider);
    if (geminiProvider) {
      return (typeof saved === "string" && saved.startsWith("gemini-")) ? saved : "gemini-3.1-pro-preview";
    }
    if (provider !== "local") return saved || "gemini-2.5-pro";
    return isKnownLocalModel(saved) ? saved : "ggml";
  });
  const [ocrHost, setOcrHost] = useState(() => localStorage.getItem("ocrHost") || "gpu");
  const [gradingHost, setGradingHost] = useState(() => localStorage.getItem("gradingHost") || "mac");
  const aiStatus = useAiStatus(8000);

  // ── Rubric for selected folder ──────────────────────────────────────────
  const [autoRubricText, setAutoRubricText] = useState("");
  const [autoPdfRubric, setAutoPdfRubric] = useState(null);
  const [rubricMetadata, setRubricMetadata] = useState(null);
  const [rubricPreviewExpanded, setRubricPreviewExpanded] = useState(false);

  // ── Processing ──────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);
  const [progress, setProgress] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const stopRef = useRef(false);
  const alertSoundRef = useRef(null);

  // ── Results ─────────────────────────────────────────────────────────────
  const [recheckResults, setRecheckResults] = useState(() => {
    try {
      const saved = localStorage.getItem("recheckResults");
      return saved ? JSON.parse(saved) : [];
    } catch {
      localStorage.removeItem("recheckResults");
      return [];
    }
  });
  const [selectedResults, setSelectedResults] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [viewingComment, setViewingComment] = useState(null);

  // ── Approve ─────────────────────────────────────────────────────────────
  const [approveForm, setApproveForm] = useState({ class: "", section: "", subject: "" });
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [studentLists, setStudentLists] = useState([]);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveListDateFilter, setApproveListDateFilter] = useState("");
  const [persistBusy, setPersistBusy] = useState(false);

  const [message, setMessage] = useState({ show: false, text: "", type: "" });

  const folderPath = selectedFolderId ? `rubrics/${selectedFolderId}` : "";
  const [pageProvider, setPageProvider] = useState(() => readPageProvider(CHECK_PROVIDER_KEY));
  const isLocal = pageProvider === "local";
  const provider = pageProvider;
  const changePageProvider = (value) => {
    setPageProvider(value);
    writePageProvider(CHECK_PROVIDER_KEY, value);
  };
  const isGemini = provider === "gemini";
  useEffect(() => {
    if (pageProvider === "local") {
      setOcrModel((cur) => (isKnownLocalModel(cur) ? cur : "qwen38_27b"));
      setGradingModel((cur) => (isKnownLocalModel(cur) ? cur : "ggml"));
    } else if (pageProvider === "gemini") {
      setOcrModel((cur) => (typeof cur === "string" && cur.startsWith("gemini-") ? cur : "gemini-3.1-pro-preview"));
      setGradingModel((cur) => (typeof cur === "string" && cur.startsWith("gemini-") ? cur : "gemini-3.1-pro-preview"));
    } else if (pageProvider === "minimax") {
      const ok = new Set(["", "MiniMax-M2", "MiniMax-M1", "MiniMax-Text-01"]);
      setOcrModel((cur) => (ok.has(cur) ? cur : ""));
      setGradingModel((cur) => (ok.has(cur) ? cur : ""));
    }
  }, [pageProvider]);

  const parsedBatchSizes = useMemo(() => parseBatchSizes(batchSizesRaw), [batchSizesRaw]);
  const batchSizes = useMemo(
    () => resolveSheetCounts(parsedBatchSizes, selectedImages.length),
    [parsedBatchSizes, selectedImages.length]
  );
  // Each sheet is front+back → 2 images.
  const batchSum = useMemo(() => batchSizes.reduce((a, b) => a + b * 2, 0), [batchSizes]);
  const studentCount = batchSizes.length;
  const isUniformBatch = parsedBatchSizes.length === 1;

  const showMessage = useCallback((text, type = "info") => setMessage({ show: true, text, type }), []);
  const mediumOption = subjectType === "none"
    ? "none"
    : `${subjectType === "urdu" ? "urdu" : "english"}_${pipelineMode === "two_step" ? "two_step" : "single"}`;
  const handleMediumChange = (value) => {
    setSubjectType(value.startsWith("urdu") ? "urdu" : "other");
    setPipelineMode(value.endsWith("two_step") ? "two_step" : "single");
  };

  // Persist folder + results
  useEffect(() => {
    try {
      if (selectedFolderId) localStorage.setItem("recheckTab_selectedFolderId", selectedFolderId);
      else localStorage.removeItem("recheckTab_selectedFolderId");
    } catch { /* ignore */ }
  }, [selectedFolderId]);

  useEffect(() => {
    localStorage.setItem("recheckResults", JSON.stringify(recheckResults));
  }, [recheckResults]);

  useEffect(() => {
    localStorage.setItem("recheckPipelineMode", pipelineMode);
  }, [pipelineMode]);

  useEffect(() => {
    try { localStorage.setItem("urduOcrModel", ocrModel || ""); } catch { /* ignore */ }
  }, [ocrModel]);
  useEffect(() => {
    try { localStorage.setItem("urduGradingModel", gradingModel || ""); } catch { /* ignore */ }
  }, [gradingModel]);
  useEffect(() => {
    try { localStorage.setItem("ocrHost", ocrHost || ""); } catch { /* ignore */ }
  }, [ocrHost]);
  useEffect(() => {
    try { localStorage.setItem("gradingHost", gradingHost || ""); } catch { /* ignore */ }
  }, [gradingHost]);

  useEffect(() => {
    fetchScansFolders();
    fetchStudentLists();
  }, []);

  useEffect(() => {
    if (!selectedFolderId) {
      setFolderImages([]);
      setSelectedImages([]);
      return;
    }
    loadFolderImages(selectedFolderId);
    fetchRubricForFolder(`rubrics/${selectedFolderId}`);
  }, [selectedFolderId]);

  const fetchScansFolders = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/scans/folders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const folders = (data.folders || [])
        .filter((f) => f.type !== "mcq")
        .sort((a, b) => {
          const dateA = new Date(a.test_date || a.created_at || 0);
          const dateB = new Date(b.test_date || b.created_at || 0);
          return dateB - dateA;
        });
      setScansFolders(folders);
    } catch (err) {
      console.error("Error fetching folders:", err);
    }
  };

  const loadFolderImages = async (rubricId) => {
    setImagesLoading(true);
    setSelectedImages([]);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/scans/${rubricId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load folder images");
      const data = await res.json();
      const scans = (data.scans || [])
        .slice()
        .sort((a, b) => String(a.file_name || "").localeCompare(String(b.file_name || ""), undefined, { numeric: true }));
      setFolderImages(scans);
    } catch (err) {
      console.error(err);
      showMessage(err.message || "Failed to load images", "error");
      setFolderImages([]);
    } finally {
      setImagesLoading(false);
    }
  };

  const fetchRubricForFolder = async (path) => {
    try {
      const match = path.match(/rubrics\/(\d+)/);
      if (!match) return;
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/rubrics/${match[1]}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const rubric = data.rubric;
      if (!rubric) return;
      setAutoRubricText(rubric.content || "");
      setRubricMetadata({
        class: rubric.class || "",
        section: rubric.section || "",
        subject: rubric.subject || "",
        test_date: rubric.test_date || null,
      });
      if (rubric.issues_json) {
        try {
          const issues = JSON.parse(rubric.issues_json);
          if (issues.pages && issues.pages.length > 0) {
            setAutoPdfRubric({ pages: issues.pages, text: rubric.content || "" });
          } else {
            setAutoPdfRubric(null);
          }
        } catch {
          setAutoPdfRubric(null);
        }
      } else {
        setAutoPdfRubric(null);
      }
    } catch (err) {
      console.error("Error loading rubric:", err);
    }
  };

  const fetchStudentLists = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/students/lists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const sortedLists = (data || []).sort((a, b) => {
          const titleA = `${a.class || ""}-${a.section || ""} ${a.subject || ""}`;
          const titleB = `${b.class || ""}-${b.section || ""} ${b.subject || ""}`;
          return titleA.localeCompare(titleB, undefined, { numeric: true, sensitivity: "base" });
        });
        setStudentLists(sortedLists);
      }
    } catch (err) {
      console.error("Error fetching student lists:", err);
    }
  };

  const toggleImage = (name) => {
    setSelectedImages((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const selectAllImages = () => {
    setSelectedImages(folderImages.map((s) => s.file_name).filter(Boolean));
  };

  const clearImageSelection = () => setSelectedImages([]);

  /** Group selected images (in selection order) by sheet counts (×2 images). */
  const buildImageGroups = useCallback(() => {
    if (batchSizes.length === 0) return null;
    if (batchSum !== selectedImages.length) return null;
    const groups = [];
    let offset = 0;
    for (const sheets of batchSizes) {
      const imgCount = sheets * 2;
      groups.push({
        images: selectedImages.slice(offset, offset + imgCount),
        sheet_count: sheets,
      });
      offset += imgCount;
    }
    return groups;
  }, [batchSizes, batchSum, selectedImages]);

  const previewGroups = useMemo(() => {
    if (batchSizes.length === 0 || batchSum !== selectedImages.length) return [];
    return (buildImageGroups() || []).map((g) => g.images);
  }, [batchSizes, batchSum, selectedImages, buildImageGroups]);

  // ── Start check ─────────────────────────────────────────────────────────
  const handleStartCheck = async () => {
    if (!folderPath) return showMessage("Select a folder first", "error");
    if (selectedImages.length === 0) return showMessage("Select at least one image", "error");
    if (parsedBatchSizes.length === 0) {
      return showMessage("Enter batch sizes (e.g. 1 or 1,3,2)", "error");
    }
    if (batchSizes.length === 0 || batchSum !== selectedImages.length) {
      if (isUniformBatch) {
        const sheets = parsedBatchSizes[0];
        const imgsPer = sheets * 2;
        return showMessage(
          `Uniform batch ${sheets} sheet(s) = ${imgsPer} image(s) per student. ` +
          `Selected ${selectedImages.length} image(s) must be a multiple of ${imgsPer}.`,
          "error"
        );
      }
      return showMessage(
        `Batch sum (${batchSizes.reduce((a, b) => a + b, 0)} sheet(s) → ${batchSum || parsedBatchSizes.reduce((a, b) => a + b * 2, 0)} images) ` +
        `must equal selected images (${selectedImages.length})`,
        "error"
      );
    }
    if (subjectType === "none" || subjectType === "") {
      return showMessage("Please select a Medium Type", "error");
    }
    if (!isLocal && isGemini && !selectedApiKey) {
      return showMessage("Please select a Limit (MR1/RC1)", "error");
    }
    if (isLocal && pipelineMode !== "two_step" && !selectedModel) {
      return showMessage("Please select a local model", "error");
    }
    if (pipelineMode === "two_step" && (!ocrModel || !gradingModel)) {
      return showMessage("Please select OCR and Grading models", "error");
    }
    if (isGemini && pipelineMode !== "two_step" && !selectedModel) {
      return showMessage("Please select a Model", "error");
    }
    if (isGemini && pipelineMode === "two_step" && (!ocrModel || !gradingModel)) {
      return showMessage("Please select OCR and Grading models", "error");
    }

    const activeRubricText = autoRubricText || rubricText;
    const activePdfRubric = autoPdfRubric || pdfRubric;
    if (!activeRubricText && !activePdfRubric) {
      return showMessage("No rubric found for this folder", "error");
    }

    const image_groups = buildImageGroups();
    if (!image_groups) return showMessage("Could not build student groups", "error");

    const systemPrompt =
      subjectType === "urdu"
        ? buildPromptUrdu(activeRubricText || "", activePdfRubric)
        : buildPromptForBatch(activeRubricText || "", activePdfRubric);

    setLoading(true);
    setStopRequested(false);
    stopRef.current = false;
    setProgress(`Starting check for ${studentCount} student(s)...`);

    try {
      const token = localStorage.getItem("authToken");
      const body = {
        folder_path: folderPath,
        image_groups,
        limit: selectedApiKey || "MR1",
        model: isLocal ? "simple" : selectedModel,
        subject_type: subjectType,
        system_prompt: systemPrompt,
        pdf_rubric: activePdfRubric || null,
        provider,
        source: "check_more",
        enable_thinking: false,
        ...(isLocal ? { local_model: pipelineMode === "two_step" ? ocrModel : selectedModel } : {}),
        ...(pipelineMode === "two_step" ? {
          pipeline: "two_step",
          ocr_model: ocrModel,
          grading_model: gradingModel,
          ...(isLocal && ocrHost ? { ocr_host: ocrHost } : {}),
          ...(isLocal && gradingHost ? { grading_host: gradingHost } : {}),
          ocr_system_prompt: subjectType === "urdu"
            ? buildPromptUrduOcr(activeRubricText || "", activePdfRubric)
            : buildPromptOcrEnglish(activeRubricText || "", activePdfRubric),
          grading_system_prompt: subjectType === "urdu"
            ? buildPromptUrduGrading(activeRubricText || "", activePdfRubric)
            : buildPromptEnglishGrading(activeRubricText || "", activePdfRubric),
          ocr_unclear_threshold: 0.30,
          ocr_enable_thinking: false,
          grading_enable_thinking: false,
        } : {}),
      };

      const res = await fetch(`${API_BASE_URL}/assess/check-direct`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to start check");
      }

      const data = await res.json();
      const sid = data.session_id;
      setSessionId(sid);
      setProgress(`Checking 0 / ${data.total || studentCount}...`);

      await pollUntilDone(sid, data.total || studentCount);
    } catch (err) {
      console.error(err);
      showMessage(err.message || "Check failed", "error");
      setLoading(false);
      setProgress("");
    }
  };

  const pollUntilDone = async (sid, total) => {
    const token = localStorage.getItem("authToken");
    while (!stopRef.current) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const res = await fetch(`${API_BASE_URL}/assess/progress/${sid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) continue;
        const data = await res.json();
        const prog = data.progress || data;
        const completed = prog.completed ?? prog.processed ?? 0;
        const status = prog.status || "";
        setProgress(`Checking ${completed} / ${prog.total || total}...`);

        if (status === "completed" || status === "failed" || status === "cancelled") {
          await loadSessionResults(sid);
          setLoading(false);
          setSessionId(null);
          if (status === "completed") {
            setProgress(`Done — ${completed} student(s) checked`);
            showMessage(`Checked ${completed} student(s)`, "success");
            if (alertSoundRef.current) alertSoundRef.current.play().catch(() => {});
          } else {
            setProgress(`Stopped (${status})`);
            showMessage(`Check ${status}`, status === "cancelled" ? "info" : "error");
          }
          return;
        }
      } catch (err) {
        console.error("Poll error:", err);
      }
    }
    // Stop requested
    try {
      await fetch(`${API_BASE_URL}/assess/cancel/${sid}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* ignore */ }
    await loadSessionResults(sid);
    setLoading(false);
    setSessionId(null);
    setProgress("Stopped");
  };

  const loadSessionResults = async (sid) => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(
        `${API_BASE_URL}/assess/results?session_id=${encodeURIComponent(sid)}&source=check_more`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const rows = data.data || [];
      if (rows.length === 0) return;
      setRecheckResults((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const mapped = rows
          .filter((r) => !existingIds.has(r.id))
          .map((r) => ({
            id: r.id,
            student_name: r.student_name,
            image_name: r.image_name,
            class: r.class,
            section: r.section,
            subject: r.subject,
            marks: r.marks,
            comments: r.comments,
            table_comments: r.table_comments,
            status: r.status || "success",
            folder_path: r.folder_path || folderPath,
            ocr_text: r.ocr_text,
            source: r.source || "check_more",
            sheet_count: null,
          }));
        return [...prev, ...mapped];
      });
    } catch (err) {
      console.error("Failed to load session results:", err);
    }
  };

  const handleStop = () => {
    setStopRequested(true);
    stopRef.current = true;
  };

  // ── Cell edit ───────────────────────────────────────────────────────────
  const handleCellEdit = (id, field, currentValue) => {
    setEditingCell({ id, field, value: currentValue == null ? "" : String(currentValue) });
  };
  const handleCellChange = (value) => {
    setEditingCell((prev) => (prev ? { ...prev, value } : prev));
  };
  const commitCellEdit = () => {
    if (!editingCell) return;
    const { id, field, value } = editingCell;
    setRecheckResults((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const updated = { ...r, [field]: value };
        if (field === "marks") {
          updated.status = value && String(value).toLowerCase() !== "error" ? "success" : "error";
        }
        return updated;
      })
    );
    setEditingCell(null);
  };

  // ── Persist: snapshot / saved folder / approve ──────────────────────────
  const getSelectedRows = () =>
    recheckResults.filter((r) => selectedResults.includes(r.id));

  /** Add to assessment folder snapshot (append only). */
  const handleAddToSnapshot = async () => {
    const rows = getSelectedRows();
    if (rows.length === 0) return showMessage("Select results to add", "error");
    const fp = rows[0].folder_path || folderPath;
    if (!fp) return showMessage("No folder path on selected rows", "error");
    setPersistBusy(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/assess/snapshots/append`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          folder_path: fp,
          rows: rows.map((r) => ({
            student_name: r.student_name,
            image_name: r.image_name,
            class: r.class,
            section: r.section,
            subject: r.subject,
            marks: r.marks,
            comments: r.comments,
            table_comments: r.table_comments,
            status: r.status || "success",
            source: r.source || "check_more",
            ocr_text: r.ocr_text,
            grading_host: r.grading_host || null,
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to append snapshot");
      showMessage(data.message || `Added ${rows.length} row(s) to snapshot`, "success");
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setPersistBusy(false);
    }
  };

  /**
   * Add to saved Sort folder (analyzed_paper_snapshots + live analyzed_papers).
   * Append only — never replaces existing saved rows.
   */
  const handleAddToSavedFolder = async () => {
    const rows = getSelectedRows();
    if (rows.length === 0) return showMessage("Select results to add", "error");
    const fp = rows[0].folder_path || folderPath;
    if (!fp) return showMessage("No folder path on selected rows", "error");
    setPersistBusy(true);
    try {
      const token = localStorage.getItem("authToken");
      // Derive sort rows: one analyzed_papers row per checked student.
      // sheet_count from how many images were in that student's batch (parsed
      // from comments "[N sheets]" when present, else 1).
      const sortRows = rows.map((r, i) => {
        const sheetMatch = String(r.comments || "").match(/\[(\d+)\s*sheets?\]/i);
        const sheetCount = sheetMatch ? parseInt(sheetMatch[1], 10) : 1;
        const codeMatch = String(r.student_name || "").match(/\(([^)]+)\)/);
        const uniqueCode = codeMatch ? codeMatch[1] : `R${Date.now()}_${i}`;
        return {
          roll_name: r.student_name,
          image_no: r.image_name,
          unique_code: uniqueCode,
          sheet_count: sheetCount,
          batch_size_final: sheetCount * 2,
          model_type: null,
          limit_type: null,
        };
      });

      const res = await fetch(`${API_BASE_URL}/analyze/snapshots/append`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ folder_path: fp, rows: sortRows }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to append to saved folder");
      showMessage(data.message || `Added ${rows.length} row(s) to saved folder`, "success");
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setPersistBusy(false);
    }
  };

  /** Approve into Students — ADD marks only, never clear existing list data. */
  const handleApprove = async () => {
    setApproveLoading(true);
    try {
      const { class: className, section, subject } = approveForm;
      if (!className || !section || !subject) {
        showMessage("Please select a student list", "error");
        return;
      }
      if (selectedResults.length === 0) {
        showMessage("Please select results to approve", "error");
        return;
      }

      const listName = `${className} - ${section} - ${subject}`;
      const selectedData = recheckResults
        .filter((r) => selectedResults.includes(r.id))
        .map((r) => ({
          name: r.student_name,
          pdf_name: r.image_name,
          marks: r.marks,
          comments: r.comments ? `${r.comments} | ${listName}` : `${listName}`,
        }));

      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/students/approve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          class: className,
          section,
          subject,
          checkedData: selectedData,
        }),
      });
      if (response.ok) {
        const result = await response.json();
        let msg = result.message || "Approved";
        if (result.unmatched && result.unmatched.length > 0) {
          msg += `. ${result.unmatched.length} unmatched - saved to Mistakes tab`;
        }
        showMessage(msg, "success");
        setShowApproveForm(false);
        setRecheckResults((prev) => prev.filter((r) => !selectedResults.includes(r.id)));
        setSelectedResults([]);
        localStorage.setItem(
          "lastApprovedAssessmentLabel",
          `${className}-${section} ${subject}`.trim()
        );
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to approve");
      }
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setApproveLoading(false);
    }
  };

  const getListDisplayedDate = (list) => {
    for (const key of ["checked_date", "checked_time", "created_at"]) {
      if (!list[key]) continue;
      try {
        const date = new Date(list[key]);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" });
        }
      } catch { /* continue */ }
    }
    return null;
  };

  const getApproveUniqueDates = () => {
    const dates = new Set();
    studentLists.forEach((l) => {
      const d = getListDisplayedDate(l);
      if (d) dates.add(d);
    });
    return [...dates].sort().reverse();
  };

  const filteredApproveLists = !approveListDateFilter
    ? studentLists
    : studentLists.filter((list) => getListDisplayedDate(list) === approveListDateFilter);

  // Anchor for shift-click range selection in the results table (same pattern
  // as the Check tab): plain click sets the anchor; shift-click selects every
  // row between the anchor and the clicked row (inclusive).
  const lastSelectedIndexRef = useRef(null);

  const handleSelectResult = (id, rowIndex = null, shiftKey = false) => {
    if (shiftKey && lastSelectedIndexRef.current !== null && Number.isInteger(rowIndex)) {
      const anchor = lastSelectedIndexRef.current;
      const start = Math.min(anchor, rowIndex);
      const end = Math.max(anchor, rowIndex);
      const rangeIds = recheckResults.slice(start, end + 1).map((r) => r.id);
      setSelectedResults((prev) => {
        const next = new Set(prev);
        for (const rid of rangeIds) next.add(rid);
        return Array.from(next);
      });
      return;
    }

    setSelectedResults((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    if (Number.isInteger(rowIndex)) lastSelectedIndexRef.current = rowIndex;
  };

  const selectAllResults = () => {
    setSelectedResults(recheckResults.map((r) => r.id));
  };

  const handleDeleteResult = (id) => {
    setRecheckResults((prev) => prev.filter((r) => r.id !== id));
    setSelectedResults((prev) => prev.filter((x) => x !== id));
  };

  const handleClearResults = () => {
    if (!window.confirm("Clear all recheck results from this tab?")) return;
    setRecheckResults([]);
    setSelectedResults([]);
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="assessment-tab" style={{ width: "100%", maxWidth: "none", padding: "0.4rem 0.5rem", boxSizing: "border-box" }}>
      <audio ref={alertSoundRef} src="/alert.mp3" preload="auto" />

      <div style={{ marginBottom: "var(--spacing-xl)" }}>
        <h1 style={{ fontSize: "2rem", fontWeight: 700, color: "var(--text-primary)", marginBottom: "0.5rem" }}>
          Recheck Student Sheets
        </h1>
        <p style={{ fontSize: "0.95rem", color: "var(--text-muted)" }}>
          Pick a scans folder, select images, set sheet batches (e.g. <code>1</code> for all students, or <code>1,3,2</code> per student), then send to the local model. No sorted table required.
        </p>
      </div>

      <RecheckFolderBrowser
        folders={scansFolders}
        selectedFolderId={selectedFolderId}
        onSelect={setSelectedFolderId}
        onMessage={showMessage}
      />

      {/* Image selection */}
      {selectedFolderId && (
        <div className="card" style={{ marginBottom: "var(--spacing-xl)" }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 12, marginBottom: "var(--spacing-md)",
          }}>
            <h2 style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>
              2. Select Images
              <span style={{ fontWeight: 500, fontSize: "0.9rem", color: "var(--text-muted)", marginLeft: 8 }}>
                {selectedImages.length} / {folderImages.length} selected
              </span>
            </h2>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn-common" onClick={selectAllImages} disabled={imagesLoading}>
                Select all
              </button>
              <button type="button" className="btn-common" onClick={clearImageSelection} disabled={selectedImages.length === 0}>
                Clear
              </button>
            </div>
          </div>

          {imagesLoading ? (
            <div style={{ padding: 24, color: "var(--text-muted)" }}>Loading images…</div>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: 6,
              maxHeight: 320,
              overflowY: "auto",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
            }}>
              {folderImages.map((scan) => {
                const name = scan.file_name;
                const checked = selectedImages.includes(name);
                const order = checked ? selectedImages.indexOf(name) + 1 : null;
                return (
                  <label
                    key={name}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "6px 8px", borderRadius: 6, cursor: "pointer",
                      background: checked ? "rgba(59,130,246,0.12)" : "transparent",
                      border: checked ? "1px solid #3B82F6" : "1px solid transparent",
                      fontSize: "0.8rem", color: "var(--text-primary)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleImage(name)}
                    />
                    {order != null && (
                      <span style={{
                        minWidth: 18, height: 18, borderRadius: 9, background: "#3B82F6",
                        color: "white", fontSize: 11, fontWeight: 700,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {order}
                      </span>
                    )}
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>
                      {name}
                    </span>
                  </label>
                );
              })}
              {folderImages.length === 0 && (
                <div style={{ padding: 12, color: "var(--text-muted)" }}>No images in this folder</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Batches + config + run */}
      {selectedFolderId && (
        <div className="checking-split-layout" style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 10 }}>
        <div className="checking-left-col" style={{ flex: "0 1 calc(30% - 12px)", minWidth: 300 }}>
        <div className="card" style={{ marginBottom: 0, padding: 10 }}>
          {/* Check-tab style rubric status: compact and colocated with checking
              controls. The full rubric is hidden until explicitly opened. */}
          <div style={{
            border: "1px solid #6ee7b7", background: "rgba(16,185,129,0.08)",
            borderRadius: 8, padding: "7px 9px", marginBottom: 9,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, color: "#065f46", fontSize: 12, fontWeight: 700 }}>
                <FontAwesomeIcon icon={faFileAlt} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {(autoRubricText || autoPdfRubric || rubricText || pdfRubric)
                    ? `${rubricMetadata?.class || "Rubric"}${rubricMetadata?.section ? `-${rubricMetadata.section}` : ""}${rubricMetadata?.subject ? ` - ${rubricMetadata.subject}` : ""}`
                    : "No Rubric Loaded"}
                </span>
              </span>
              {(autoRubricText || autoPdfRubric || rubricText || pdfRubric) && (
                <button type="button" onClick={() => setRubricPreviewExpanded((v) => !v)}
                  title={rubricPreviewExpanded ? "Hide rubric preview" : "Show rubric preview"}
                  style={{
                    width: 25, height: 25, flexShrink: 0, borderRadius: 13,
                    border: rubricPreviewExpanded ? 0 : "1px solid #93c5fd",
                    background: rubricPreviewExpanded ? "#2563eb" : "rgba(255,255,255,.7)",
                    color: rubricPreviewExpanded ? "white" : "#1e40af", cursor: "pointer",
                  }}>
                  <i className={`fa-solid fa-${rubricPreviewExpanded ? "eye-slash" : "eye"}`} />
                </button>
              )}
            </div>
            {rubricPreviewExpanded && (
              <div style={{
                marginTop: 7, paddingTop: 7, borderTop: "1px solid rgba(16,185,129,.25)",
                maxHeight: 150, overflowY: "auto", whiteSpace: "pre-wrap",
                fontSize: 11, lineHeight: 1.45, color: "var(--text-secondary)",
              }}>
                {autoPdfRubric || pdfRubric
                  ? `PDF Rubric loaded (${(autoPdfRubric || pdfRubric).pages?.length || 0} pages)\n${(autoPdfRubric || pdfRubric).text || ""}`
                  : stripQuestionPaperText(autoRubricText || rubricText)}
              </div>
            )}
          </div>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--text-primary)", marginBottom: "var(--spacing-md)" }}>
            3. Batches &amp; Check
          </h2>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "var(--spacing-lg)",
            marginBottom: "var(--spacing-lg)",
          }}>
            <div>
              <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
                Sheets per student
              </label>
              <input
                type="text"
                value={batchSizesRaw}
                onChange={(e) => setBatchSizesRaw(e.target.value)}
                placeholder="e.g. 1 or 1,3,2"
                style={{
                  width: "100%", padding: "0.75rem", borderRadius: 8,
                  border: "1px solid var(--border)", fontSize: "1rem",
                  backgroundColor: "var(--select-bg, white)", color: "var(--text-primary)",
                }}
              />
              <div style={{ marginTop: 8, fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                {studentCount > 0 ? (
                  <>
                    <strong>{studentCount} student{studentCount !== 1 ? "s" : ""}</strong>
                    {" · "}
                    {isUniformBatch
                      ? `${parsedBatchSizes[0]} sheet${parsedBatchSizes[0] !== 1 ? "s" : ""} each`
                      : `sheets ${batchSizes.join(",")}`}
                    {" · "}{batchSum} images
                    {selectedImages.length > 0 && (
                      <span style={{ color: batchSum === selectedImages.length ? "#059669" : "#dc2626", marginLeft: 6 }}>
                        {batchSum === selectedImages.length
                          ? `(matches ${selectedImages.length} selected)`
                          : `(need ${selectedImages.length} selected)`}
                      </span>
                    )}
                  </>
                ) : parsedBatchSizes.length === 1 && selectedImages.length > 0 ? (
                  <>
                    Uniform <strong>{parsedBatchSizes[0]}</strong> sheet
                    {parsedBatchSizes[0] !== 1 ? "s" : ""} = {parsedBatchSizes[0] * 2} images/student.
                    {" "}Selected {selectedImages.length} must be a multiple of {parsedBatchSizes[0] * 2}.
                  </>
                ) : (
                  "Enter 1 to give every student 1 sheet (2 images), or 1,3,2 for mixed sizes"
                )}
              </div>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "var(--text-secondary)", marginBottom: 6 }}>
                Medium
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 6 }}>
                {[
                  { value: "english_single", label: "English-1", sub: "single", color: "#3b82f6" },
                  { value: "english_two_step", label: "English-2", sub: "two-step", color: "#2563eb" },
                  { value: "urdu_single", label: "Urdu-1", sub: "single", color: "#8b5cf6" },
                  { value: "urdu_two_step", label: "Urdu-2", sub: "two-step", color: "#7c3aed" },
                ].map((option) => {
                  const selected = mediumOption === option.value;
                  return (
                    <button key={option.value} type="button" onClick={() => handleMediumChange(option.value)}
                      style={{
                        padding: "7px 8px", borderRadius: 6,
                        border: selected ? `2px solid ${option.color}` : subjectType === "none" ? "1px solid #f59e0b" : "1px solid var(--border)",
                        background: selected ? option.color : subjectType === "none" ? "#fffbeb" : "var(--select-bg,white)",
                        color: selected ? "white" : subjectType === "none" ? "#92400e" : "var(--text-primary)",
                        fontWeight: 700, fontSize: 12, cursor: "pointer",
                        display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1,
                      }}>
                      <span>{option.label}</span>
                      <span style={{ fontSize: 9, fontWeight: 500, opacity: 0.8 }}>{option.sub}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <ProviderSelect value={pageProvider} onChange={changePageProvider} style={{ width: "100%" }} />

            {isGemini && (
              <div>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
                  Limit
                </label>
                <select
                  value={selectedApiKey}
                  onChange={(e) => setSelectedApiKey(e.target.value)}
                  style={{
                    width: "100%", padding: "0.75rem", borderRadius: 8,
                    border: "1px solid var(--border)", fontSize: "1rem",
                    backgroundColor: "var(--select-bg, white)", color: "var(--text-primary)",
                  }}
                >
                  <option value="">Select</option>
                  <option value="MR1">MR1</option>
                  <option value="RC1">RC1</option>
                </select>
              </div>
            )}

            {pipelineMode === "two_step" && (isLocal || isGemini) && (
              <div style={{ gridColumn: "1 / -1" }}>
                <TwoStepAiControls
                  title="OCR / grading models & hosts"
                  ocrModel={ocrModel}
                  setOcrModel={setOcrModel}
                  gradingModel={gradingModel}
                  setGradingModel={setGradingModel}
                  ocrHost={ocrHost}
                  setOcrHost={setOcrHost}
                  gradingHost={gradingHost}
                  setGradingHost={setGradingHost}
                  hosts={aiStatus?.local?.hosts || []}
                  isLocalProvider={isLocal}
                  isGeminiProvider={isGemini}
                  isMinimaxProvider={provider === "minimax"}
                  showHosts={isLocal}
                />
              </div>
            )}

            {isLocal && pipelineMode !== "two_step" && (
              <div>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
                  Local Model
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  style={{
                    width: "100%", padding: "0.75rem", borderRadius: 8,
                    border: selectedModel ? "2px solid #10b981" : "1px solid var(--border)",
                    fontSize: "1rem",
                    backgroundColor: selectedModel ? "#ECFDF5" : "var(--select-bg, white)",
                    color: "var(--text-primary)",
                  }}
                >
                  <option value="">Select model</option>
                  {LOCAL_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            )}

            {isGemini && pipelineMode !== "two_step" && (
              <div>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
                  Model
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  style={{
                    width: "100%", padding: "0.75rem", borderRadius: 8,
                    border: "1px solid var(--border)", fontSize: "1rem",
                    backgroundColor: "var(--select-bg, white)", color: "var(--text-primary)",
                  }}
                >
                  <option value="">Select model</option>
                  <option value="gemini-3.8-flash">3.8-flash</option>
                  <option value="gemini-3.6-flash">3.6-flash</option>
                  <option value="gemini-3.1-pro-preview">3.1-pro-preview (2-12)</option>
                  <option value="gemini-3.1-flash-lite-preview">3.1-flash-lite-preview (0.25-1.5)</option>
                  <option value="gemini-3-flash-preview">3-flash-preview (0.5-3)</option>
                  <option value="gemini-2.5-pro">2.5-pro (1.25-10)</option>
                  <option value="gemini-2.5-flash">2.5-flash (0.3-2.5)</option>
                  <option value="gemini-2.5-flash-lite">2.5-flash-lite (0.1-0.4)</option>
                </select>
              </div>
            )}
          </div>

          {previewGroups.length > 0 && (
            <div style={{
              marginBottom: 16, padding: 12, borderRadius: 8,
              background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.3)",
              fontSize: "0.85rem", color: "var(--text-secondary)",
            }}>
              <strong style={{ color: "var(--text-primary)" }}>{studentCount} students:</strong>
              <ol style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                {previewGroups.map((g, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    Student {i + 1}: {batchSizes[i] || Math.ceil(g.length / 2)} sheet
                    {(batchSizes[i] || Math.ceil(g.length / 2)) !== 1 ? "s" : ""}
                    {" "}({g.length} image{g.length !== 1 ? "s" : ""}) — {g.join(", ")}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {progress && (
            <div style={{ marginBottom: 12, fontSize: "0.95rem", color: "var(--text-secondary)" }}>
              {progress}
            </div>
          )}

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              className="btn-green"
              onClick={handleStartCheck}
              disabled={loading || selectedImages.length === 0}
              style={{ padding: "0.9rem 1.4rem", fontWeight: 700, borderRadius: 10 }}
            >
              {loading ? (
                <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 8 }} />Checking…</>
              ) : (
                <><FontAwesomeIcon icon={faRocket} style={{ marginRight: 8 }} />Start Check</>
              )}
            </button>
            {loading && (
              <button
                type="button"
                onClick={handleStop}
                style={{
                  padding: "0.9rem 1.4rem", fontWeight: 700, borderRadius: 10,
                  background: "#EF4444", color: "white", border: "none", cursor: "pointer",
                }}
              >
                <FontAwesomeIcon icon={faStop} style={{ marginRight: 8 }} />
                Stop
              </button>
            )}
          </div>
        </div>
        </div>

        {/* Check-style right column: the checking/results table remains visible
            beside Medium and batch controls, including before the first run. */}
        <div className="checking-right-col" style={{ flex: "1 1 calc(70% - 12px)", minWidth: 520 }}>

      {/* Results */}
      {recheckResults.length > 0 && (
        <div className="card" style={{ marginBottom: 0, padding: 10 }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 12, marginBottom: 16,
          }}>
            <h2 style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>
              Results ({recheckResults.length})
            </h2>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" className="btn-common" onClick={selectAllResults}>Select all</button>
              <button type="button" className="btn-common" onClick={() => setSelectedResults([])}>Clear selection</button>
              <button
                type="button"
                className="btn-common"
                onClick={handleAddToSnapshot}
                disabled={persistBusy || selectedResults.length === 0}
                title="Append to assessment snapshot for this folder (no overwrite)"
              >
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
                Add to Snapshot
              </button>
              <button
                type="button"
                className="btn-common"
                onClick={handleAddToSavedFolder}
                disabled={persistBusy || selectedResults.length === 0}
                title="Append to Sort saved folder + live sorted list (no overwrite)"
              >
                <FontAwesomeIcon icon={faFolder} style={{ marginRight: 6 }} />
                Add to Saved Folder
              </button>
              <button
                type="button"
                className="btn-green"
                onClick={() => setShowApproveForm(true)}
                disabled={selectedResults.length === 0}
                title="Approve into Students — adds marks, does not clear existing"
              >
                <FontAwesomeIcon icon={faCheck} style={{ marginRight: 6 }} />
                Approve ({selectedResults.length})
              </button>
              <button type="button" onClick={handleClearResults} style={{
                background: "transparent", border: "1px solid #EF4444", color: "#EF4444",
                padding: "8px 12px", borderRadius: 8, cursor: "pointer",
              }}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
              <thead>
                <tr style={{ background: "var(--surface)" }}>
                  <th style={thStyle} />
                  <th style={thStyle}>Student</th>
                  <th style={thStyle}>Image</th>
                  <th style={thStyle}>Class</th>
                  <th style={thStyle}>Section</th>
                  <th style={thStyle}>Subject</th>
                  <th style={thStyle}>Marks</th>
                  <th style={thStyle}>Comments</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recheckResults.map((result, rowIndex) => {
                  const isEditing = (field) => editingCell?.id === result.id && editingCell?.field === field;
                  const isSelected = selectedResults.includes(result.id);
                  return (
                    <tr
                      key={result.id}
                      onClick={(e) => {
                        const tag = (e.target.tagName || "").toLowerCase();
                        if (["input", "button", "select", "textarea", "a", "label"].includes(tag)) return;
                        if (editingCell && editingCell.id === result.id) return;
                        handleSelectResult(result.id, rowIndex, e.shiftKey);
                      }}
                      style={{
                        background: isSelected ? "rgba(37,99,235,0.08)" : undefined,
                        cursor: "pointer",
                      }}
                    >
                      <td style={tdStyle}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectResult(result.id, rowIndex, e.shiftKey);
                            e.preventDefault();
                          }}
                          onChange={() => {}}
                          style={{ cursor: "pointer" }}
                          title="Click to toggle · Shift+click to select a range"
                        />
                      </td>
                      <td
                        style={{ ...tdStyle, cursor: "pointer" }}
                        onDoubleClick={() => handleCellEdit(result.id, "student_name", result.student_name)}
                      >
                        {isEditing("student_name") ? (
                          <input autoFocus value={editingCell.value} onChange={(e) => handleCellChange(e.target.value)}
                            onBlur={commitCellEdit} onKeyDown={(e) => e.key === "Enter" && commitCellEdit()}
                            style={editorStyle} />
                        ) : (result.student_name || "—")}
                      </td>
                      <td style={tdStyle}>{result.image_name || "—"}</td>
                      <td
                        style={{ ...tdStyle, cursor: "pointer" }}
                        onDoubleClick={() => handleCellEdit(result.id, "class", result.class)}
                      >
                        {isEditing("class") ? (
                          <input autoFocus value={editingCell.value} onChange={(e) => handleCellChange(e.target.value)}
                            onBlur={commitCellEdit} onKeyDown={(e) => e.key === "Enter" && commitCellEdit()}
                            style={editorStyle} />
                        ) : (result.class || "—")}
                      </td>
                      <td
                        style={{ ...tdStyle, cursor: "pointer" }}
                        onDoubleClick={() => handleCellEdit(result.id, "section", result.section)}
                      >
                        {isEditing("section") ? (
                          <input autoFocus value={editingCell.value} onChange={(e) => handleCellChange(e.target.value)}
                            onBlur={commitCellEdit} onKeyDown={(e) => e.key === "Enter" && commitCellEdit()}
                            style={editorStyle} />
                        ) : (result.section || "—")}
                      </td>
                      <td
                        style={{ ...tdStyle, cursor: "pointer" }}
                        onDoubleClick={() => handleCellEdit(result.id, "subject", result.subject)}
                      >
                        {isEditing("subject") ? (
                          <input autoFocus value={editingCell.value} onChange={(e) => handleCellChange(e.target.value)}
                            onBlur={commitCellEdit} onKeyDown={(e) => e.key === "Enter" && commitCellEdit()}
                            style={editorStyle} />
                        ) : (result.subject || "—")}
                      </td>
                      <td
                        style={{ ...tdStyle, fontWeight: 600, cursor: "pointer", color: result.status === "error" ? "#f87171" : "#34d399" }}
                        onDoubleClick={() => handleCellEdit(result.id, "marks", result.marks)}
                      >
                        {isEditing("marks") ? (
                          <input autoFocus value={editingCell.value} onChange={(e) => handleCellChange(e.target.value)}
                            onBlur={commitCellEdit} onKeyDown={(e) => e.key === "Enter" && commitCellEdit()}
                            style={editorStyle} />
                        ) : (result.marks ?? "—")}
                      </td>
                      <td
                        style={{ ...tdStyle, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}
                        onDoubleClick={() => handleCellEdit(result.id, "comments", result.comments)}
                        title={result.comments || ""}
                      >
                        {isEditing("comments") ? (
                          <textarea autoFocus rows={2} value={editingCell.value}
                            onChange={(e) => handleCellChange(e.target.value)}
                            onBlur={commitCellEdit}
                            style={{ ...editorStyle, resize: "vertical" }} />
                        ) : (result.comments || "—")}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                          <button type="button" onClick={() => setViewingComment(result)}
                            style={{ background: "#3B82F6", color: "white", border: "none", padding: "6px 10px", borderRadius: 6, cursor: "pointer" }}>
                            <i className="fa-solid fa-eye" />
                          </button>
                          <button type="button" onClick={() => handleDeleteResult(result.id)}
                            style={{ background: "#EF4444", color: "white", border: "none", padding: "6px 10px", borderRadius: 6, cursor: "pointer" }}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {recheckResults.length === 0 && (
        <div className="card" style={{
          minHeight: 320, marginBottom: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", textAlign: "center",
          color: "var(--text-muted)", border: "1px solid var(--border)",
        }}>
          <i className="fa-solid fa-table-list" style={{ fontSize: 34, color: "#94a3b8", marginBottom: 10 }} />
          <div style={{ fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Checking Results</div>
          <div style={{ fontSize: 13 }}>Select images and batches, choose a Medium on the left, then start checking.</div>
        </div>
      )}
        </div>
        </div>
      )}

      {/* Approve modal */}
      {showApproveForm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}>
          <div style={{
            background: "var(--surface, white)", borderRadius: 12, padding: 24, maxWidth: 520, width: "100%",
            color: "var(--text-primary)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Approve to Students</h3>
              <button type="button" onClick={() => setShowApproveForm(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18 }}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: 12 }}>
              Adds marks for {selectedResults.length} selected row(s). Existing student-list marks are <strong>not</strong> cleared.
            </p>
            <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: 6 }}>Filter by date</label>
            <select
              value={approveListDateFilter}
              onChange={(e) => setApproveListDateFilter(e.target.value)}
              style={{ width: "100%", padding: 10, marginBottom: 12, borderRadius: 8, border: "1px solid var(--border)" }}
            >
              <option value="">All dates</option>
              {getApproveUniqueDates().map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: 6 }}>Student list</label>
            <select
              value={approveForm.class && approveForm.section && approveForm.subject
                ? `${approveForm.class}|${approveForm.section}|${approveForm.subject}` : ""}
              onChange={(e) => {
                const [c, s, sub] = e.target.value.split("|");
                setApproveForm({ class: c || "", section: s || "", subject: sub || "" });
              }}
              style={{ width: "100%", padding: 10, marginBottom: 16, borderRadius: 8, border: "1px solid var(--border)" }}
            >
              <option value="">Select list…</option>
              {filteredApproveLists.map((l) => (
                <option key={`${l.class}|${l.section}|${l.subject}`} value={`${l.class}|${l.section}|${l.subject}`}>
                  {l.class}-{l.section} {l.subject} ({l.student_count || "?"} students)
                </option>
              ))}
            </select>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" className="btn-common" onClick={() => setShowApproveForm(false)}>Cancel</button>
              <button type="button" className="btn-green" onClick={handleApprove} disabled={approveLoading}>
                {approveLoading ? "Approving…" : "Approve (add only)"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments modal */}
      {viewingComment && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }} onClick={() => setViewingComment(null)}>
          <div
            style={{ background: "var(--surface, white)", borderRadius: 12, padding: 24, maxWidth: 640, width: "100%", maxHeight: "80vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>{viewingComment.student_name}</h3>
              <button type="button" onClick={() => setViewingComment(null)} style={{ background: "none", border: "none", cursor: "pointer" }}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: "0.9rem", color: "var(--text-secondary)" }}>
              {viewingComment.comments || "(no comments)"}
            </pre>
          </div>
        </div>
      )}

      {/* Message modal */}
      {message.show && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1100,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}>
          <div style={{
            background: "var(--surface, white)", borderRadius: 12, padding: 24, maxWidth: 420, width: "100%",
            borderLeft: `4px solid ${message.type === "error" ? "#EF4444" : message.type === "success" ? "#10b981" : "#3B82F6"}`,
          }}>
            <p style={{ margin: "0 0 16px", color: "var(--text-primary)" }}>{message.text}</p>
            <button type="button" className="btn-common" onClick={() => setMessage({ show: false, text: "", type: "" })}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: 10,
  border: "1px solid var(--border, #e5e7eb)",
  textAlign: "left",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: 10,
  border: "1px solid var(--border, #e5e7eb)",
  color: "var(--text-primary)",
};

const editorStyle = {
  width: "100%",
  padding: 6,
  borderRadius: 4,
  border: "1px solid #3B82F6",
  fontSize: "0.9rem",
};
